import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Background, Controls, Edge, Handle, Node, Position, ReactFlow, ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Section } from './DetailPrimitives';
import {
    OntologyKind, categoryForConceptType, conceptTypeLabel, formatValue, scopeLevelColor,
} from './ontologyFormat';
import { getOntologyGraph, normalizeGraph } from '../../api/ontologyApi';
import { layoutWithDagre } from '../../utils/dagreLayout';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The mesh immediately around one record.
 *
 * A NEIGHBOURHOOD, not the library. 262 concepts plus their policies and thresholds is a
 * many-to-many hairball that answers nothing; one or two hops from the record in front of
 * you answers what people actually ask — what governs this, what configures it, and what
 * would break if I changed it.
 *
 * Mounted from the three detail bodies exactly like UsedByTemplatesSection, and self-hides
 * when the server returns nothing but the focus node: a diagram of one box is not worth the
 * vertical space.
 */

const NODE_WIDTH = 210;
const NODE_HEIGHT = 74;

const KIND_ACCENT: Record<string, string> = {
    concepts: '#7DD3FC',
    policies: '#C4B5FD',
    parameters: '#FFDE59',
};

const EDGE_LABEL: Record<string, string> = {
    governs: 'governs',
    sets: 'sets',
    under: 'under',
    broader: 'broader',
    narrower: 'narrower',
    related: 'related',
    detected_by: 'detected by',
};

interface GraphNodeData {
    kind: OntologyKind;
    code: string;
    label: string;
    concept_type?: string;
    value?: unknown;
    unit?: string | null;
    scope_level?: string;
    status?: string;
    /** Approved AND within its effective window — what the engine is actually running. */
    in_force?: boolean;
    is_focus?: boolean;
    flags?: string[];
    [key: string]: unknown;
}

/** What the node says under its title — the one fact that matters for its kind. */
const subtitleOf = (data: GraphNodeData): string => {
    if (data.kind === 'parameters') return formatValue(data.value, data.unit);
    if (data.kind === 'policies') return 'Policy';
    const category = categoryForConceptType(data.concept_type);
    return category ? category.label.replace(/s$/, '') : conceptTypeLabel(data.concept_type);
};

const MeshNode = ({ data }: { data: GraphNodeData }) => {
    const accent = KIND_ACCENT[data.kind] ?? '#94A3B8';
    const flagged = Boolean(data.flags?.length);
    // Drafts and future-dated versions are drawn — you can open one, so you can see its
    // mesh — but they are not what the engine runs, and the node must not imply they are.
    const proposed = data.in_force === false;

    return (
        <Box
            sx={{
                width: NODE_WIDTH,
                height: NODE_HEIGHT,
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: data.is_focus ? dsVar('raised') : dsVar('surface'),
                // A gap is marked where the record is, not only in the coverage report.
                border: `1px ${proposed ? 'dashed' : 'solid'} ${
                    flagged ? ink('#FBBF24') : data.is_focus ? dsVar('accent-fg') : dsVar('line')}`,
                boxShadow: data.is_focus ? `0 0 0 1px ${dsVar('accent-fg')}` : 'none',
                opacity: proposed ? 0.85 : 1,
                cursor: 'pointer',
                '&:hover': { borderColor: accent },
            }}
        >
            {/* Both handles are needed for edges to attach, but this is a read-only
                diagram — nobody draws connections, so they are invisible. */}
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography
                    sx={{
                        fontSize: '0.5625rem', fontWeight: 600, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: ink(accent),
                    }}
                >
                    {subtitleOf(data)}
                </Typography>
                {proposed && (
                    <Typography
                        sx={{
                            fontSize: '0.5625rem', fontWeight: 600, letterSpacing: '0.06em',
                            textTransform: 'uppercase', color: '#FBBF24',
                        }}
                    >
                        · {data.status === 'approved' ? 'not yet in force' : 'draft'}
                    </Typography>
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 500, lineHeight: 1.3,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {data.label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: '"JetBrains Mono", monospace', fontSize: '0.5625rem',
                    color: ink(scopeLevelColor(data.scope_level)), mt: 0.25,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
            >
                {data.code}
            </Typography>

            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
        </Box>
    );
};

// Module scope, never inside render — a new object identity here remounts every node on
// every render and throws away the layout.
const nodeTypes = { meshNode: MeshNode };

/**
 * React Flow's stylesheet is light-themed, so the node chrome reset and the Controls
 * restyle are both mandatory. SCOPED to this component's wrapper class: the equivalent
 * block in WorkflowVisualization uses bare `!important` selectors, which would reach into
 * any other React Flow instance mounted on the same page.
 */
const SCOPED_STYLES = `
.ontology-mesh .react-flow__node { background: transparent !important; border: none !important;
    padding: 0 !important; box-shadow: none !important; width: auto !important; }
.ontology-mesh .react-flow__edge path { stroke: rgb(var(--ds-line)) !important; stroke-width: 1.5 !important; }
.ontology-mesh .react-flow__edge:hover path { stroke: rgb(var(--ds-accent-fg)) !important; }
.ontology-mesh .react-flow__edge-text { fill: rgb(var(--ds-fg-muted)) !important; font-size: 9px !important; }
.ontology-mesh .react-flow__edge-textbg { fill: rgb(var(--ds-page)) !important; }
.ontology-mesh .react-flow__controls { background-color: rgb(var(--ds-surface)) !important;
    border: 1px solid rgb(var(--ds-line)) !important; border-radius: 8px !important; box-shadow: none !important; }
.ontology-mesh .react-flow__controls button { background-color: transparent !important;
    border: none !important; border-bottom: 1px solid rgb(var(--ds-line)) !important; color: rgb(var(--ds-fg-2)) !important; }
.ontology-mesh .react-flow__controls button:hover { background-color: rgb(var(--ds-raised)) !important; }
.ontology-mesh .react-flow__controls button svg { fill: currentColor !important; }
.ontology-mesh .react-flow__attribution { display: none !important; }
`;

interface OntologyMeshSectionProps {
    kind: OntologyKind;
    code?: string | null;
}

const MeshCanvas: React.FC<OntologyMeshSectionProps> = ({ kind, code }) => {
    const navigate = useNavigate();
    const [depth, setDepth] = useState(1);
    const [graph, setGraph] = useState<ReturnType<typeof normalizeGraph> | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!code) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            try {
                const res = await getOntologyGraph(
                    { kind, code, depth }, { signal: controller.signal });
                if (active) setGraph(normalizeGraph(res));
            } catch {
                // Silent: this is one section of a detail page that is otherwise complete,
                // and a diagram failing is not worth an error banner over the record.
                if (active) setGraph(null);
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [kind, code, depth]);

    const { nodes, edges } = useMemo(() => {
        if (!graph) return { nodes: [], edges: [] };

        // Annotated rather than inferred: the API module is JS, so `graph.nodes` arrives as
        // any[] and the layout helper's generic would otherwise widen to its constraint.
        const flowNodes: Node[] = graph.nodes.map((node: any) => ({
            id: node.id,
            type: 'meshNode',
            position: { x: 0, y: 0 },
            data: node as GraphNodeData,
        }));

        const flowEdges: Edge[] = graph.edges.map((edge: any) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: 'smoothstep',
            label: EDGE_LABEL[edge.type] ?? edge.type,
            labelShowBg: true,
            markerEnd: { type: 'arrowclosed', color: dsVar('fg-faint'), width: 14, height: 14 },
        }));

        return {
            nodes: layoutWithDagre(flowNodes, flowEdges, {
                nodeWidth: NODE_WIDTH, nodeHeight: NODE_HEIGHT, rankdir: 'LR', ranksep: 130,
            }),
            edges: flowEdges,
        };
    }, [graph]);

    // A diagram of the focus node alone teaches nothing — the record page already says
    // everything on it. Hide rather than render an empty canvas.
    if (!loading && nodes.length <= 1) return null;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), mr: 'auto' }}>
                    {graph?.truncated
                        ? 'Showing the closest part of a larger neighbourhood.'
                        : 'Click any record to open it.'}
                </Typography>

                {[1, 2].map((hops) => (
                    <Box
                        key={hops}
                        component="button"
                        type="button"
                        aria-current={depth === hops ? 'true' : undefined}
                        onClick={() => setDepth(hops)}
                        sx={{
                            px: 1.25, py: 0.4,
                            border: '1px solid',
                            borderColor: depth === hops ? dsVar('accent-fg') : dsVar('line'),
                            borderRadius: '6px', cursor: 'pointer',
                            fontFamily: '"Inter", sans-serif', fontSize: '0.75rem',
                            color: depth === hops ? dsVar('accent-fg') : dsVar('fg-2'),
                            backgroundColor: depth === hops ? 'rgba(255,222,89,0.08)' : 'transparent',
                        }}
                    >
                        {hops} hop{hops > 1 ? 's' : ''}
                    </Box>
                ))}
            </Box>

            <Box
                className="ontology-mesh"
                sx={{
                    height: 420, borderRadius: '8px', overflow: 'hidden',
                    border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('page'),
                    position: 'relative',
                }}
            >
                <style dangerouslySetInnerHTML={{ __html: SCOPED_STYLES }} />

                {loading && (
                    <Box sx={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 1,
                        backgroundColor: dsVar('page', 0.6),
                    }}>
                        <CircularProgress size={22} sx={{ color: dsVar('accent-fg') }} />
                    </Box>
                )}

                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    onNodeClick={(_event, node) => {
                        const data = node.data as GraphNodeData;
                        navigate(`/ontology/${data.kind}/${encodeURIComponent(data.code)}`);
                    }}
                    // No `duration`: an animating viewport that outlives unmount is the
                    // requestAnimationFrame-after-destroy crash the cytoscape view
                    // documents, and switching records here unmounts mid-animation.
                    fitView
                    fitViewOptions={{ padding: 0.15 }}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    proOptions={{ hideAttribution: true }}
                >
                    <Background color={dsVar('raised')} gap={20} size={1} />
                    <Controls showInteractive={false} position="bottom-right" />
                </ReactFlow>
            </Box>
        </Box>
    );
};

const OntologyMeshSection: React.FC<OntologyMeshSectionProps> = ({ kind, code }) => {
    if (!code) return null;
    return (
        <Section title="How this connects" always>
            <ReactFlowProvider>
                <MeshCanvas kind={kind} code={code} />
            </ReactFlowProvider>
        </Section>
    );
};

export default OntologyMeshSection;
